import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen, Search, Star, Bell, Users, FileText, TrendingUp, Radio, Library, BookMarked } from 'lucide-react';
import axios from 'axios';
import TestimonialsCarousel from '@/components/TestimonialsCarousel';
import LatestCallsCarousel from '@/components/LatestCallsCarousel';
import LatestBlogCarousel from '@/components/LatestBlogCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ 
    total_users: 0, 
    active_calls: 0, 
    calls_today: 0,
    calls_last_7_days: 0,
    users_online: 0,
    journals_mapped: 0
  });
  const [testimonials, setTestimonials] = useState([]);
  const [heroContent, setHeroContent] = useState({ title: 'Quero Publicar', content: '' });

  // Get or create visitor ID for tracking
  const getVisitorId = () => {
    let visitorId = localStorage.getItem('visitor_id');
    if (!visitorId) {
      visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('visitor_id', visitorId);
    }
    return visitorId;
  };

  // Send visitor ping to track presence
  const sendVisitorPing = async () => {
    try {
      const visitorId = getVisitorId();
      await axios.post(`${API_URL}/api/visitor/ping`, {}, {
        headers: { 'X-Visitor-ID': visitorId }
      });
    } catch (error) {
      // Silent fail - don't interrupt user experience
    }
  };

  useEffect(() => {
    fetchStats();
    fetchTestimonials();
    fetchHeroContent();
    sendVisitorPing();
    
    // Auto-refresh stats every 15 seconds for real-time updates
    const statsInterval = setInterval(fetchStats, 15000);
    
    // Send visitor ping every 30 seconds to maintain presence
    const pingInterval = setInterval(sendVisitorPing, 30000);
    
    return () => {
      clearInterval(statsInterval);
      clearInterval(pingInterval);
    };
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(response.data.testimonials);
    } catch (error) {
      console.error('Failed to fetch testimonials');
    }
  };

  const fetchHeroContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/content/landing_hero`);
      setHeroContent(response.data);
    } catch (error) {
      console.error('Failed to fetch hero content');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section py-24 px-6">
        <div className="max-w-6xl mx-auto text-center text-white">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6">
            {/* Logo SVG - Transparência perfeita */}
            <div className="flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 md:h-20 md:w-20 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 mx-auto sm:mx-0">
              <BookMarked className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-white" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-center">
              {heroContent.title}
            </h1>
          </div>
          <p className="text-lg lg:text-xl mb-8 max-w-3xl mx-auto font-light">
            {heroContent.content || 'Plataforma especializada em conectar pesquisadores brasileiros com oportunidades de publicação em revistas científicas, com classificação QUALIS/CAPES'}
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8">
            {/* Visitantes Online (ao vivo) - sempre mostrar */}
            <div className="bg-green-500/20 backdrop-blur-sm rounded-md px-4 sm:px-5 py-3 border border-green-400/30">
              <div className="flex items-center gap-2 text-white">
                <div className="relative">
                  <Radio className="h-4 w-4 text-green-300" />
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-green-400 rounded-full animate-ping" />
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-green-400 rounded-full" />
                </div>
                <span className="text-lg sm:text-xl font-bold">{Math.max(1, stats.users_online)}</span>
              </div>
              <p className="text-xs text-green-200 mt-1">Online agora</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-md px-4 sm:px-5 py-3 border border-white/20">
              <div className="flex items-center gap-2 text-white">
                <Users className="h-4 w-4" />
                <span className="text-lg sm:text-xl font-bold">{stats.total_users}</span>
              </div>
              <p className="text-xs text-white/80 mt-1">Pesquisadores cadastrados</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-md px-4 sm:px-5 py-3 border border-white/20">
              <div className="flex items-center gap-2 text-white">
                <FileText className="h-4 w-4" />
                <span className="text-lg sm:text-xl font-bold">{stats.active_calls}</span>
              </div>
              <p className="text-xs text-white/80 mt-1">Chamadas Ativas</p>
            </div>
            
            {/* Revistas Mapeadas */}
            <div className="bg-purple-500/20 backdrop-blur-sm rounded-md px-4 sm:px-5 py-3 border border-purple-400/30">
              <div className="flex items-center gap-2 text-white">
                <Library className="h-4 w-4 text-purple-300" />
                <span className="text-lg sm:text-xl font-bold">{stats.journals_mapped}</span>
              </div>
              <p className="text-xs text-purple-200 mt-1">Revistas Mapeadas</p>
            </div>
            
            {/* Novas chamadas nos últimos 7 dias */}
            {stats.calls_last_7_days > 0 && (
              <div className="bg-blue-500/20 backdrop-blur-sm rounded-md px-4 sm:px-5 py-3 border border-blue-400/30">
                <div className="flex items-center gap-2 text-white">
                  <TrendingUp className="h-4 w-4 text-blue-300" />
                  <span className="text-lg sm:text-xl font-bold">+{stats.calls_last_7_days}</span>
                </div>
                <p className="text-xs text-blue-200 mt-1">Novas chamadas nos últimos 7 dias</p>
              </div>
            )}
          </div>

          <div className="flex gap-4 justify-center">
            <Button
              data-testid="hero-login-btn"
              size="lg"
              onClick={() => navigate('/auth')}
              className="bg-white text-[#002FA7] hover:bg-gray-100 font-semibold px-8 py-6 flex items-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Entrar com Google gratuitamente
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel - Before Features */}
      <TestimonialsCarousel testimonials={testimonials} />

      {/* Latest Calls Carousel */}
      <LatestCallsCarousel />

      {/* Blog Carousel */}
      <LatestBlogCarousel />

      {/* Features Section */}
      <section className="py-16 px-6 bg-[#F9F9F8]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold tracking-tight text-center mb-12 text-[#0A0A0A]">
            Recursos da Plataforma
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 border border-[#E4E4E7] rounded-md">
              <Search className="w-10 h-10 text-[#002FA7] mb-4" />
              <h3 className="text-xl font-bold mb-2">Busca Inteligente</h3>
              <p className="text-[#4B4C52]">
                Use IA para encontrar chamadas compatíveis com seu resumo científico
              </p>
            </div>
            <div className="bg-white p-6 border border-[#E4E4E7] rounded-md">
              <BookOpen className="w-10 h-10 text-[#002FA7] mb-4" />
              <h3 className="text-xl font-bold mb-2">Classificação QUALIS</h3>
              <p className="text-[#4B4C52]">
                Filtre por classificação QUALIS/CAPES de A1 até C
              </p>
            </div>
            <div className="bg-white p-6 border border-[#E4E4E7] rounded-md">
              <Star className="w-10 h-10 text-[#002FA7] mb-4" />
              <h3 className="text-xl font-bold mb-2">Favoritos</h3>
              <p className="text-[#4B4C52]">
                Salve chamadas importantes e acesse facilmente
              </p>
            </div>
            <div className="bg-white p-6 border border-[#E4E4E7] rounded-md">
              <Bell className="w-10 h-10 text-[#002FA7] mb-4" />
              <h3 className="text-xl font-bold mb-2">Lembretes por Email</h3>
              <p className="text-[#4B4C52]">
                Receba lembretes 1 dia antes do prazo da chamada expirar
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#002FA7] text-white py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-6">
            <div>
              <h3 className="font-bold mb-3">Quero Publicar</h3>
              <p className="text-sm text-white/80">Conectando pesquisadores com oportunidades de publicação</p>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Institucional</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => navigate('/about')} className="text-white/80 hover:text-white">
                    Quem Somos
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/contact')} className="text-white/80 hover:text-white">
                    Fale Conosco
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Apoie</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => navigate('/donation')} className="text-white/80 hover:text-white">
                    Faça uma Doação
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => navigate('/terms')} className="text-white/80 hover:text-white">
                    Termos de Uso
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/privacy')} className="text-white/80 hover:text-white">
                    Privacidade
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <h4 className="font-semibold mb-3">Acesso</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => navigate('/auth')} className="text-white/80 hover:text-white">
                    Entrar / Cadastrar
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/dashboard')} className="text-white/80 hover:text-white">
                    Buscar Chamadas
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-6 text-center">
            <p className="text-sm">© 2026 Quero Publicar. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
